import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  fr: {
    loading: "Chargement…",
    notFound: "Livestream introuvable",
    backHome: "← Accueil",
    livestreamOfficial: "LIVESTREAM OFFICIEL",
    eventStart: "Début",
    eventEnd: "Fin",
    timezone: "Fuseau",
    chooseAccess: "Choisis ton accès",
    replayIncluded: "Replay inclus",
    replayHours: "h",
    liveOnly: "Direct uniquement",
    selectThis: "Choisir",
    selected: "✓ Sélectionné",
    yourInfo: "Tes informations",
    fullName: "Nom complet",
    email: "Courriel",
    total: "Total",
    proceedPay: "Procéder au paiement",
    processing: "Traitement…",
    cancelled: "Paiement annulé. Aucun montant n'a été prélevé.",
    cancelledTitle: "Paiement annulé",
    selectProduct: "Sélectionne un accès",
    emailRequired: "Courriel obligatoire",
    nameRequired: "Nom obligatoire",
    securePay: "Paiement sécurisé via Stripe",
    languageToggle: "EN",
    devicesNote: "Compatible mobile, tablette, ordinateur",
    accessLink: "Tu recevras un lien d'accès personnel par courriel après le paiement.",
    by: "Présenté par",
    org: "Institut Impact Élite Martial · KAIZEN System",
  },
  en: {
    loading: "Loading…",
    notFound: "Livestream not found",
    backHome: "← Home",
    livestreamOfficial: "OFFICIAL LIVESTREAM",
    eventStart: "Start",
    eventEnd: "End",
    timezone: "Timezone",
    chooseAccess: "Choose your access",
    replayIncluded: "Replay included",
    replayHours: "h",
    liveOnly: "Live only",
    selectThis: "Select",
    selected: "✓ Selected",
    yourInfo: "Your details",
    fullName: "Full name",
    email: "Email",
    total: "Total",
    proceedPay: "Proceed to payment",
    processing: "Processing…",
    cancelled: "Payment cancelled. You weren't charged.",
    cancelledTitle: "Payment cancelled",
    selectProduct: "Select an access",
    emailRequired: "Email required",
    nameRequired: "Name required",
    securePay: "Secure payment via Stripe",
    languageToggle: "FR",
    devicesNote: "Compatible mobile, tablet, computer",
    accessLink: "You'll receive a personal access link by email after payment.",
    by: "Presented by",
    org: "Institut Impact Élite Martial · KAIZEN System",
  },
};

export default function LivestreamEventPublic() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [lang, setLang] = useState("fr");
  const [event, setEvent] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPid, setSelectedPid] = useState(null);
  const [customer, setCustomer] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const cancelled = searchParams.get("cancelled") === "1";
  const t = T[lang];

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/livestream/public/${slug}`);
        setEvent(r.data.event);
        setProducts(r.data.products);
        if (r.data.products.length > 0) setSelectedPid(r.data.products[0].id);
      } catch {
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const selectedProduct = products.find((p) => p.id === selectedPid);

  const checkout = async () => {
    if (!selectedPid) { toast.error(t.selectProduct); return; }
    if (!customer.email) { toast.error(t.emailRequired); return; }
    if (!customer.name) { toast.error(t.nameRequired); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/livestream/public/${slug}/checkout`, {
        product_id: selectedPid,
        customer_email: customer.email,
        customer_name: customer.name,
        language: lang,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center" data-testid="livestream-loading">{t.loading}</div>;
  if (!event) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="text-2xl font-bold" data-testid="livestream-not-found">📺 {t.notFound}</div>
        <Link to="/" className="text-amber-400 hover:underline">{t.backHome}</Link>
      </div>
    );
  }

  const startDate = event.start_at ? new Date(event.start_at) : null;
  const endDate = event.end_at ? new Date(event.end_at) : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="livestream-event-page">
      {/* Top bar */}
      <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between border-b border-zinc-900">
        <Link to="/" className="text-xs tracking-[0.3em] text-amber-400 hover:text-amber-300">KAIZEN SYSTEM · LIVESTREAM</Link>
        <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-xs border border-zinc-700 px-3 py-1.5 rounded-full hover:border-amber-500 hover:text-amber-400 transition" data-testid="lang-toggle-btn">
          {t.languageToggle}
        </button>
      </div>

      {cancelled && (
        <div className="max-w-5xl mx-auto px-4 mt-4">
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-xl p-4" data-testid="cancelled-banner">
            <div className="text-sm font-bold text-amber-300">⚠️ {t.cancelledTitle}</div>
            <div className="text-xs text-amber-200/80 mt-1">{t.cancelled}</div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-10 pb-8">
        <div className="text-[10px] tracking-[0.4em] text-red-400 mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
          {t.livestreamOfficial}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="event-title">
          {lang === "fr" ? event.title_fr : event.title_en}
        </h1>
        <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
          <Detail label={t.eventStart} value={startDate ? startDate.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—"} />
          {endDate && <Detail label={t.eventEnd} value={endDate.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { timeStyle: "short" })} />}
          <Detail label="📺" value={t.devicesNote} />
        </div>
        {(lang === "fr" ? event.description_fr : event.description_en) && (
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-3xl" data-testid="event-description">
            {lang === "fr" ? event.description_fr : event.description_en}
          </p>
        )}
      </div>

      {/* Products */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">{t.chooseAccess.toUpperCase()}</div>
        <div className="space-y-3" data-testid="products-list">
          {products.map((p) => {
            const isSel = selectedPid === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedPid(p.id)}
                className={`w-full text-left border rounded-xl p-5 transition ${isSel ? "border-amber-500/60 bg-amber-500/10" : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-600"}`}
                data-testid={`product-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold">{lang === "fr" ? p.name_fr : p.name_en}</h3>
                      {p.replay_included ? (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">
                          {t.replayIncluded} ({p.replay_duration_hours}{t.replayHours})
                        </span>
                      ) : (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 border border-zinc-700 rounded-full px-2 py-0.5">
                          {t.liveOnly}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-zinc-400 mt-1">{lang === "fr" ? p.description_fr : p.description_en}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-amber-300">{p.price_cad.toFixed(2)} $</div>
                    <div className={`text-[10px] mt-1 ${isSel ? "text-amber-400" : "text-zinc-500"}`}>{isSel ? t.selected : t.selectThis}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {selectedProduct && (
          <div className="mt-10 border border-amber-500/30 rounded-2xl p-6 bg-gradient-to-br from-amber-500/10 to-amber-500/5" data-testid="checkout-block">
            <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">{t.yourInfo.toUpperCase()}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t.fullName} *</Label>
                <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="bg-black border-zinc-800" data-testid="customer-name-input" />
              </div>
              <div>
                <Label className="text-xs">{t.email} *</Label>
                <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="bg-black border-zinc-800" data-testid="customer-email-input" />
              </div>
            </div>
            <div className="mt-5 border-t border-amber-500/20 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">{t.total}</span>
                <span className="text-3xl font-bold text-amber-300" data-testid="checkout-total">{selectedProduct.price_cad.toFixed(2)} $ CAD</span>
              </div>
              <Button onClick={checkout} disabled={busy} className="mt-4 w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-6 text-base" data-testid="checkout-btn">
                {busy ? t.processing : t.proceedPay} →
              </Button>
              <div className="mt-3 text-[10px] text-zinc-500 text-center">🔒 {t.securePay}</div>
              <div className="mt-2 text-[10px] text-zinc-400 text-center">{t.accessLink}</div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-zinc-900 py-8 mt-10">
        <div className="max-w-5xl mx-auto px-4 text-center text-zinc-500 text-xs">
          <div>{t.by}</div>
          <div className="text-amber-400 mt-1 tracking-[0.2em] font-semibold">{t.org}</div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/50">
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className="text-zinc-100 font-semibold mt-1">{value}</div>
    </div>
  );
}
